import {
  View,
  Text,
  Switch,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import { ActivityIndicator, Provider as ProviderPaper } from "react-native-paper";
import { useNavigation } from "@react-navigation/native";
import Header from "../../components/Header";
import ViewportProvider from "../../hooks/ViewPortProvider";
import PickerColorHex from "../../components/PickerColorHex";
import { UploadImages } from "../../components/UploadImages";
import AdminInput from "../../components/AdminInput";
import { useAppMobile } from "./useAppMobile";
import { PhoneMockup } from "./PhoneMockup";
import { SplashPreview, LoginPreview, HomePreview } from "./PreviewScreens";
import { ADMIN_COLORS, ADMIN_FONT } from "../../config/theme/adminColors";
import {
  SectionCard,
  Field,
  FieldLabel,
  Row,
  Col,
  InfoBanner,
} from "../AdminPanel/Products/formStyles";

// Componentes em escopo de módulo (identidade estável) para que UploadImages
// e PickerColorHex não desmontem a cada re-render — o que fecharia o modal de
// cores e resetaria o estado de upload em andamento.
const InlineImage = ({ label, tooltip, fileType, last }) => (
  <View style={[styles.inlineField, last && { marginBottom: 0 }]}>
    <FieldLabel tooltip={tooltip}>{label}</FieldLabel>
    <UploadImages fileType={fileType} />
  </View>
);

const ColorField = ({ label, tooltip, colorName, setColor, showKey, show, handleShowPicker }) => (
  <Field style={{ marginBottom: 0 }}>
    <FieldLabel tooltip={tooltip}>{label}</FieldLabel>
    <PickerColorHex
      colorName={colorName}
      props={{ color: colorName, onChange: setColor }}
      setShow={() => handleShowPicker(showKey, !show[showKey])}
      isShowPicker={show[showKey]}
      setChange={setColor}
    />
  </Field>
);

export function AppMobile() {
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const {
    show,
    handleShowPicker,
    tabBackgroundColor,
    setTabBackgroundColor,
    tabIcondColor,
    setTabIcondColor,
    tabHighlightColor,
    setTabHighlightColor,
    statusBarColor,
    setStatusBarColor,
    updateAppColors,
    loadingColorsApp,
    isSavedColorsApp,
    setIsSavedColorsApp,
    hasUnsavedColorChanges,
    isLoading,
    textAbout,
    setTextAbout,
    colorInfo,
    imageAbout,
    logoAbout,
    logoAuth,
  } = useAppMobile();

  const handleSaveColors = () => {
    updateAppColors();
    setIsSavedColorsApp(true);
    setTimeout(() => setIsSavedColorsApp(false), 2000);
  };

  const mockupWidth = 168;

  return (
    <ViewportProvider>
      <ProviderPaper>
        <View style={{ flex: 1, backgroundColor: ADMIN_COLORS.surfaceSubtle }}>
        <Header navigation={navigation} isAdminPage />

        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.page}>
            <View style={styles.headerBlock}>
              <Text style={[styles.title, isMobile && { fontSize: 24 }]}>App Mobile</Text>
              <Text style={styles.subtitle}>
                Personalize a tela de abertura e as cores exclusivas do aplicativo Android/iOS.
              </Text>
            </View>

            {isLoading ? (
              <View style={styles.loadingBox}>
                <ActivityIndicator color={ADMIN_COLORS.teal} size="large" />
              </View>
            ) : (
              <>
                <SectionCard
                  title="Tela inicial do app mobile"
                  subtitle="Imagem de abertura e logo exibidos ao abrir o aplicativo mobile. As alterações são salvas automaticamente."
                >
                  <InlineImage
                    label="Imagem de abertura (PNG)"
                    tooltip="Imagem de fundo usada na abertura do aplicativo mobile. Tamanho recomendado: 1080x1920px, limitado a 2MB. Apenas .png."
                    fileType="imageAbout"
                  />
                  <InlineImage
                    label="Logo sobre a imagem (PNG)"
                    tooltip="Logo usado no topo da tela, sobre a imagem de fundo. Tamanho recomendado: 493x337px, limitado a 2MB. Apenas .png."
                    fileType="logoAbout"
                  />
                  <Field style={{ marginBottom: 0 }}>
                    <FieldLabel>Texto sobre a imagem (opcional)</FieldLabel>
                    <AdminInput
                      placeholder="Escreva o texto aqui"
                      value={textAbout}
                      onChangeText={(text) => setTextAbout(text)}
                    />
                  </Field>
                </SectionCard>

                <SectionCard
                  title="Cores do aplicativo mobile"
                  subtitle="Personalização visual específica do aplicativo Android/iOS — não afeta a versão web."
                >
                      {hasUnsavedColorChanges && (
                        <InfoBanner variant="warn">
                          Para que suas mudanças de personalização sejam aplicadas no aplicativo, é
                          necessário clicar em "Salvar" após concluir as alterações.
                        </InfoBanner>
                      )}

                      <Row style={{ marginTop: 18, marginBottom: 18 }}>
                        <Col>
                          <ColorField
                            label="Cor de fundo da navegação inferior"
                            tooltip="Barra inferior do aplicativo onde ficam os ícones de navegação entre as telas."
                            colorName={tabBackgroundColor}
                            setColor={setTabBackgroundColor}
                            showKey="showTabBackgroundColor"
                            show={show}
                            handleShowPicker={handleShowPicker}
                          />
                        </Col>
                        <Col>
                          <ColorField
                            label="Cor dos ícones"
                            tooltip="Cor dos ícones quando não estão selecionados."
                            colorName={tabIcondColor}
                            setColor={setTabIcondColor}
                            showKey="showTabIcondColor"
                            show={show}
                            handleShowPicker={handleShowPicker}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <ColorField
                            label="Cor do ícone selecionado"
                            tooltip="Cor do ícone quando está selecionado."
                            colorName={tabHighlightColor}
                            setColor={setTabHighlightColor}
                            showKey="showTabHighlightColor"
                            show={show}
                            handleShowPicker={handleShowPicker}
                          />
                        </Col>
                        <Col>
                          <ColorField
                            label="Barra de status superior"
                            tooltip="Cor de fundo da barra de status na parte superior da tela (wifi, horário, etc). Deve ser uma cor clara, pois os ícones são pretos."
                            colorName={statusBarColor}
                            setColor={setStatusBarColor}
                            showKey="showStatusBarColor"
                            show={show}
                            handleShowPicker={handleShowPicker}
                          />
                        </Col>
                      </Row>

                      <View style={styles.saveRow}>
                        <Pressable
                          onPress={handleSaveColors}
                          style={({ hovered }) => [
                            styles.primaryBtn,
                            isSavedColorsApp && { backgroundColor: ADMIN_COLORS.success },
                            hovered && !isSavedColorsApp && { backgroundColor: ADMIN_COLORS.accentDark },
                          ]}
                        >
                          <Text style={styles.primaryBtnText}>
                            {loadingColorsApp ? "Enviando..." : isSavedColorsApp ? "Salvo!" : "Salvar"}
                          </Text>
                        </Pressable>
                      </View>
                </SectionCard>

                <SectionCard
                  title="Pré-visualização"
                  subtitle="Prévia de como as telas aparecem no aplicativo do aluno."
                >
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <View style={styles.previewRow}>
                      <PhoneMockup label="Abertura" width={mockupWidth} statusBarColor={statusBarColor.hex}>
                        <SplashPreview
                          imageUrl={imageAbout?.url}
                          logoUrl={logoAbout?.url}
                          text={textAbout}
                          buttonBg={colorInfo?.button?.backgroundAuth}
                          buttonText={colorInfo?.button?.textAuth}
                        />
                      </PhoneMockup>
                      <PhoneMockup label="Login" width={mockupWidth} statusBarColor={statusBarColor.hex}>
                        <LoginPreview
                          bgColor={colorInfo?.background?.auth}
                          textColor={colorInfo?.text?.auth}
                          buttonBg={colorInfo?.button?.backgroundAuth}
                          buttonText={colorInfo?.button?.textAuth}
                          logoUrl={logoAuth?.url}
                        />
                      </PhoneMockup>
                      <PhoneMockup label="Início" width={mockupWidth} statusBarColor={statusBarColor.hex}>
                        <HomePreview
                          appBgColor={colorInfo?.background?.app}
                          tabBgColor={tabBackgroundColor.hex}
                          tabIconColor={tabIcondColor.hex}
                          tabHighlightColor={tabHighlightColor.hex}
                        />
                      </PhoneMockup>
                    </View>
                  </ScrollView>
                </SectionCard>
              </>
            )}
          </View>
        </ScrollView>
      </View>
      </ProviderPaper>
    </ViewportProvider>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    padding: 24,
    alignItems: "center",
  },
  page: {
    width: "100%",
    maxWidth: 900,
  },
  headerBlock: {
    marginBottom: 20,
  },
  title: {
    fontFamily: ADMIN_FONT,
    fontSize: 28,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
  },
  subtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    marginTop: 4,
  },
  loadingBox: {
    paddingVertical: 80,
    alignItems: "center",
    justifyContent: "center",
  },
  inlineField: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  saveRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    marginTop: 4,
  },
  primaryBtn: {
    height: 44,
    borderRadius: 10,
    paddingHorizontal: 24,
    backgroundColor: ADMIN_COLORS.teal,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  primaryBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 15,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  previewRow: {
    flexDirection: "row",
    gap: 20,
    paddingVertical: 4,
    paddingHorizontal: 2,
  },
});
